import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Compass, Loader2, ArrowRight, PhoneCall, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { t } from "@/i18n";

/**
 * Roterende motivatie voor makelaars. Verandert élke keer dat de pagina laadt
 * én elke 12s zodat de wachttijd voelt als een korte coach-pep-talk in plaats
 * van een dood inlogscherm. Toon op vol-formaat scherm (lg+) in het linkse
 * paneel; op mobiel wordt enkel het eerste citaat boven het formulier getoond.
 */
const MOTIVATIONS = [
  {
    quote: "Eén telefoontje vandaag is één mandaat morgen.",
    hint: "Pak je lijst. Bel de eerste vijf zonder na te denken.",
  },
  {
    quote: "De makelaar die opbelt vóór de koffie, sluit vóór de lunch.",
    hint: "Vóór 10u zijn eigenaars het meest bereikbaar.",
  },
  {
    quote: "Geen mandaat zonder gesprek. Geen gesprek zonder belknop.",
    hint: "Twijfel? Bel toch. Het ergste is een 'nee'.",
  },
  {
    quote: "Elke 'nee' is een stap dichter bij de volgende 'ja'.",
    hint: "Statistisch: 1 op 7 koud contact wordt een afspraak.",
  },
  {
    quote: "Wie consequent prospecteert, hoeft niet meer te jagen.",
    hint: "30 minuten per dag. Elke werkdag. Resultaat in 90 dagen.",
  },
  {
    quote: "Een goed gesprek begint met luisteren — niet met pitchen.",
    hint: "Stel vragen. De eigenaar verkoopt zichzelf.",
  },
  {
    quote: "Je netwerk wordt vandaag gemaakt — voor over zes maanden.",
    hint: "Follow-through is geen wachten. Het is opbouwen.",
  },
  {
    quote: "De beste makelaars zijn niet de luidste — ze zijn de meest consistente.",
    hint: "Toon op, bel op, volg op. Elke dag opnieuw.",
  },
  {
    quote: "Vandaag is een leeg blad. Eén afspraak vult al de helft.",
    hint: "Begin met de twijfelaars van vorige week.",
  },
  {
    quote: "Discipline > motivatie. Plan je belblok, dan komt de energie vanzelf.",
    hint: "Zet 45 minuten in je agenda. Telefoon op stil voor de rest.",
  },
];

function pickMotivation() {
  return MOTIVATIONS[Math.floor(Math.random() * MOTIVATIONS.length)];
}

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [motivation, setMotivation] = useState(pickMotivation);
  const navigate = useNavigate();

  // Rotate motivation every 12s — niet té snel zodat ze leesbaar blijft.
  useEffect(() => {
    const id = setInterval(() => setMotivation(pickMotivation()), 12000);
    return () => clearInterval(id);
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Goedemorgen";
    if (h < 18) return "Goedemiddag";
    return "Goedenavond";
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setErr(typeof d === "string" ? d : t("auth.invalid"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-[var(--sand-50)]"
         data-testid="login-screen">
      {/* ───────────────────────── Motivation panel (left) ───────────────────────── */}
      <aside
        className="relative hidden lg:flex flex-col justify-between
                   lg:w-[58%] xl:w-[60%] p-12 xl:p-16 overflow-hidden
                   bg-[var(--teal-800,#0E5A5E)] text-white"
        data-testid="login-motivation-panel"
      >
        {/* decorative layers */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
             style={{
               backgroundImage:
                 "radial-gradient(circle at 15% 20%, rgba(255,255,255,0.6) 0%, transparent 35%), radial-gradient(circle at 85% 80%, rgba(255,255,255,0.45) 0%, transparent 40%)",
             }} />
        <div className="absolute inset-0 pointer-events-none"
             style={{
               backgroundImage:
                 "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 40%, transparent 60%, rgba(0,0,0,0.18) 100%)",
             }} />
        <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full
                        bg-[radial-gradient(circle,rgba(255,255,255,0.08)_0%,transparent_70%)]" />
        <div className="absolute -left-24 -bottom-24 w-[360px] h-[360px] rounded-full
                        bg-[radial-gradient(circle,rgba(229,201,160,0.18)_0%,transparent_70%)]" />

        {/* logo + brand */}
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-sm
                          border border-white/20 grid place-items-center">
            <Compass size={20} strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-display text-[22px] leading-tight">FlowNavigator</div>
            <div className="text-[12px] text-white/60 mt-0.5">
              Prospectie-CRM voor Vlaamse makelaars
            </div>
          </div>
        </div>

        {/* rotating motivation */}
        <div className="relative max-w-[560px]">
          <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em]
                          text-white/70 mb-5">
            <Sparkles size={13} strokeWidth={1.75} />
            <span>Vandaag</span>
          </div>
          <blockquote
            key={motivation.quote}
            data-testid="login-motivation-quote"
            className="font-display text-[34px] xl:text-[40px] leading-[1.15] tracking-tight fade-in"
          >
            "{motivation.quote}"
          </blockquote>
          <p
            key={`${motivation.quote}-hint`}
            data-testid="login-motivation-hint"
            className="mt-5 text-[15px] xl:text-[16px] text-white/75 leading-relaxed max-w-[480px] fade-in"
          >
            {motivation.hint}
          </p>
        </div>

        {/* footer stats — gives a sense of urgency */}
        <div className="relative grid grid-cols-3 gap-6 max-w-[560px]">
          <Stat icon={PhoneCall} value="30 min" label="dagelijks belblok" />
          <Stat icon={Sparkles} value="1 op 7" label="koude contacten" />
          <Stat icon={ArrowRight} value="90 dagen" label="tot eerste mandaten" />
        </div>
      </aside>

      {/* ───────────────────────── Form (right) ───────────────────────── */}
      <main className="flex-1 flex items-center justify-center px-6 py-12 sand-bg grain relative">
        <div className="w-full max-w-[400px] fade-in">
          {/* mobile-only brand */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[var(--teal-600)] text-white grid place-items-center">
              <Compass size={20} />
            </div>
            <div>
              <div className="font-display text-[22px] leading-tight">FlowNavigator</div>
              <div className="text-[12px] text-[var(--ink-500)]">{t("auth.tagline")}</div>
            </div>
          </div>

          {/* mobile-only motivation */}
          <div className="lg:hidden mb-8 p-5 rounded-2xl bg-[var(--teal-800,#0E5A5E)] text-white
                          shadow-[0_12px_32px_-16px_rgba(14,90,94,0.45)]"
               data-testid="login-motivation-mobile">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/70 mb-2">Vandaag</div>
            <p className="font-display text-[18px] leading-snug">"{motivation.quote}"</p>
            <p className="text-[13px] text-white/75 mt-2">{motivation.hint}</p>
          </div>

          <div className="text-[12px] uppercase tracking-[0.18em] text-[var(--teal-700)] mb-3
                          font-medium" data-testid="login-greeting">
            {greeting}
          </div>
          <h1 className="font-display text-[36px] leading-[1.05] text-[var(--ink-900)] mb-2">
            Klaar voor een nieuwe prospectiedag?
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mb-8">
            Meld u aan om uw pipeline en taken te openen.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="text-[12px] font-medium text-[var(--ink-700)] uppercase tracking-wider">
                {t("auth.email")}
              </span>
              <input
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="naam@kantoor.be"
                className="mt-1.5 w-full h-12 px-3.5 rounded-lg border border-[var(--border-soft)]
                           bg-white text-[15px] text-[var(--ink-900)]
                           focus:border-[var(--teal-500)] focus:ring-2 focus:ring-[var(--teal-100)]
                           outline-none transition-all"
                autoComplete="email"
              />
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-[var(--ink-700)] uppercase tracking-wider">
                {t("auth.password")}
              </span>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 w-full h-12 px-3.5 rounded-lg border border-[var(--border-soft)]
                           bg-white text-[15px] text-[var(--ink-900)]
                           focus:border-[var(--teal-500)] focus:ring-2 focus:ring-[var(--teal-100)]
                           outline-none transition-all"
                autoComplete="current-password"
              />
            </label>

            {err && (
              <div
                data-testid="login-error"
                className="text-[13px] text-[var(--danger)] bg-[#FBE4E4]
                           border border-[#E8B4B4] rounded-lg px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy}
              className="btn btn-primary w-full h-12 !text-[15px] mt-2
                         disabled:opacity-60 group"
            >
              {busy ? (
                <><Loader2 size={16} className="animate-spin" /> {t("auth.logging_in")}</>
              ) : (
                <>
                  {t("auth.login")}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <a href="#" data-testid="login-forgot-link"
                 className="text-[13px] text-[var(--teal-700)] hover:text-[var(--teal-800)]
                            hover:underline underline-offset-4">
                {t("auth.forgot")}
              </a>
            </div>
          </form>

          <div className="mt-12 text-center text-[11px] text-[var(--ink-400)]">
            © {new Date().getFullYear()} FlowNavigator · Voor de Vlaamse makelaar.
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 grid place-items-center mt-0.5">
        <Icon size={15} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-[18px] leading-none">{value}</div>
        <div className="text-[11px] text-white/65 uppercase tracking-[0.12em] mt-1.5 leading-tight">
          {label}
        </div>
      </div>
    </div>
  );
}
